import { useI18n } from '../i18n';
import styles from './Features.module.css';

export function Features() {
  const { t } = useI18n();
  const photos = t.features.photos;
  const shopping = t.features.shopping;

  return (
    <section className={styles.section} id="features">
      <h2 className={styles.heading}>{t.features.heading}</h2>

      <div className={styles.pair}>
        <article className={styles.card}>
          <h3 className={styles.cardTitle}>{photos.title}</h3>
          <p className={styles.cardBody}>{photos.body}</p>
          <div className={styles.photoRow} aria-hidden="true">
            <span className={styles.photoSlot}>
              <CameraIcon />
              <span className={styles.photoCaption}>{photos.shelfTag}</span>
            </span>
            <span className={styles.photoSlot}>
              <CameraIcon />
              <span className={styles.photoCaption}>{photos.label}</span>
            </span>
            <p className={styles.quote}>{photos.note}</p>
          </div>
        </article>

        <article className={styles.card}>
          <h3 className={styles.cardTitle}>{t.features.categories.title}</h3>
          <p className={styles.cardBody}>{t.features.categories.body}</p>
          {/*
            No colour swatches. The brand spec gives categories no colours at
            all and sets them as labels instead, because in this system colour
            already means bought or broken and cannot also be decoration.
          */}
          <div className={styles.categoryList} aria-hidden="true">
            {t.features.categories.rows.map((row, index) => (
              <span
                key={row.label}
                /* The middle row is styled as the one being dragged, which is
                   what makes "drag them into the order of your shop" legible
                   without an animation. */
                className={`${styles.categoryRow} ${index === 1 ? styles.categoryRowActive : ''}`}
              >
                <span className={styles.grip}>≡</span>
                <span className={styles.categoryName}>{row.label}</span>
                <span className={styles.categoryCount}>{row.count}</span>
              </span>
            ))}
          </div>
        </article>
      </div>

      {/*
        Shopping mode is not dark mode. It is a different room with different
        light, so this panel keeps the fixed aisle palette in both themes
        rather than following the visitor's setting.
      */}
      <article className={styles.shopping}>
        <div>
          <span className={styles.shoppingOverline}>{shopping.overline}</span>
          <h3 className={styles.shoppingTitle}>{shopping.title}</h3>
          <p className={styles.shoppingBody}>{shopping.body}</p>
          <div className={styles.stats}>
            {shopping.stats.map((stat) => (
              <span key={stat.label}>
                <span className={styles.statValue}>{stat.value}</span>
                <span className={styles.statLabel}>{stat.label}</span>
              </span>
            ))}
          </div>
        </div>

        <div className={styles.shoppingDemo} aria-hidden="true">
          <div className={styles.progressHead}>
            <span className={styles.progressCount}>
              7 <span className={styles.progressCountUnit}>{shopping.progressLabel}</span>
            </span>
            <span className={styles.shopper}>{shopping.shopper}</span>
          </div>

          <div className={styles.progressTrack}>
            <span className={styles.progressFill} />
          </div>

          <span className={styles.shopRow}>
            <span className={styles.shopCheck} />
            <span className={styles.shopName}>{shopping.items[0]}</span>
            <span className={styles.shopQty}>500g</span>
          </span>

          <span className={styles.shopRow}>
            <span className={styles.shopCheck} />
            <span className={styles.shopName}>{shopping.items[1]}</span>
            <span className={styles.shopThumb} />
          </span>

          <span className={styles.shopRowDone}>
            <span className={styles.shopCheckDone} />
            <span className={styles.shopNameDone}>{shopping.items[2]}</span>
          </span>
        </div>
      </article>
    </section>
  );
}

/* Geometric, round caps and joins, per the spec's ICONS rule. */
const CameraIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="var(--icon-soft)"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
    <circle cx="12" cy="13.5" r="3.2" />
  </svg>
);
